// @flow

import React, { type Node, useLayoutEffect, useState } from 'react';
import { useAppValues } from '@tectonics/app-values';
import { LATAM_PHONE_MASK } from '@tectonics/commons/masks.js';
import { IfFeature } from '@tectonics/prs/feature';
import { useCurrentAllFeatures } from '@tectonics/prs/payment-spec';
import { useEventBusListener, type EventListener } from '@tectonics/public-event-bus';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { type UserInputValues, useUserInputValue } from '@tectonics/prs/user-input-values';
import { useValidation, hasUserInputValidationError } from '@tectonics/prs/validation';

import { isElementVisible } from '@tectonics/look-and-feel/commons/dom-utils';
import { useFormStatusContext } from '@tectonics/prs/form-status/use-form-status-context';
import { scrollToFirstFieldWithError } from '@tectonics/screen';

import { Margin, Padding } from '../../../atoms';

import { FieldAcceptTerms } from './field-accept-terms';
import { FieldArgentineDocument } from './field-argentine-document';
import { FieldArgentineDocumentType } from './field-argentine-document-type';
import { FieldArgentineCashBankList } from './field-argentine-cash-bank-list';
import { FieldColombianDocument } from './field-colombian-document';
import { FieldColombianDocumentType } from './field-colombian-document-type';
import { FieldBrazilianDocument } from './field-brazilian-document';
import { FieldBrazilianPhoneNumber } from './field-brazilian-phone-number';
import { FieldBrazilianZipcode } from './field-brazilian-zipcode';
import { FieldUruguayanDocument } from './field-uruguayan-document';
import { FieldParaguayanDocument } from './field-paraguayan-document';
import { FieldChileanDocument } from './field-chilean-document';
import { FieldCustomerZipcode } from './field-customer-zipcode';
import { FieldCustomerPhoneNumber } from './field-customer-phone-number';
import { FieldCaptchaToken } from './field-captcha-token';
import { FieldCardExpiryDate } from './field-card-expiry-date';
import { FieldCardHolderName } from './field-card-holder-name';
import { FieldCardInstalments } from './field-card-instalments';
import { FieldCardNumber } from './field-card-number';
import { FieldCardSecurityCode } from './field-card-security-code';
import { FieldCustomerEmail } from './field-customer-email';
import { FieldEwalletSelect } from './field-ewallet-select';
import { SafePaymentMessage } from './safe-payment-message';
import { SelectedPaymentMethod } from './payment-method';
import { FieldBrazilianDebitcardInfo } from './field-brazilian-debitcard-info';
import { BankList } from './field-colombian-bank-list';

type Props = {|
  onSubmit: (boolean, boolean) => Promise<boolean>,
|};

export function Form({ onSubmit }: Props): Node {
  const allFeatures = useCurrentAllFeatures();
  const [appValues, setAppValues] = useAppValues();
  const [, setBrazilianDocument] = useUserInputValue('brazilianDocument');

  useOnSubmit({ onSubmit });

  useEventBusListener('checkout:open', (args: any) => {
    setAppValues({
      ...appValues,
      shopifyCheckoutToken: args.checkoutToken,
      shopifyCheckoutApiHost: args.checkoutApiHost,
      shopifyFacebookPixelFbp: args.facebookPixelFbp,
    });

    if (args.customerDocument) {
      const onlyNumbers = args.customerDocument.replace(/[^\d]/g, '');
      setBrazilianDocument(onlyNumbers);
    }
  });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      <SelectedPaymentMethod />

      <IfFeature allFeatures={allFeatures} wanted="field_customer_email">
        <FieldCustomerEmail />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_brazilian_phone_number">
        <FieldBrazilianPhoneNumber />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_mexican_phone_number">
        <FieldCustomerPhoneNumber
          mask={LATAM_PHONE_MASK}
          fieldName="mexicanPhoneNumber"
        />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_colombian_phone_number">
        <FieldCustomerPhoneNumber
          mask={LATAM_PHONE_MASK}
          fieldName="colombianPhoneNumber"
        />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_chilean_phone_number">
        <FieldCustomerPhoneNumber
          mask={LATAM_PHONE_MASK}
          fieldName="chileanPhoneNumber"
        />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_paraguayan_phone_number">
        <FieldCustomerPhoneNumber
          mask={LATAM_PHONE_MASK}
          fieldName="paraguayanPhoneNumber"
        />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_brazilian_zipcode">
        <FieldBrazilianZipcode />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_mexican_zipcode">
        <FieldCustomerZipcode
          mask={Array(5).fill(/\d/)}
          fieldName="mexicanZipcode"
          placeholder="_____"
        />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_colombian_zipcode">
        <FieldCustomerZipcode
          mask={Array(6).fill(/\d/)}
          fieldName="colombianZipcode"
          placeholder="______"
        />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_card_holder_name">
        <FieldCardHolderName />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_brazilian_document">
        <FieldBrazilianDocument />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_argentine_document_type">
        <FieldArgentineDocumentType />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_argentine_document">
        <FieldArgentineDocument />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_argentine_cash_bank_list">
        <FieldArgentineCashBankList />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_colombian_document_type">
        <FieldColombianDocumentType />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_colombian_document">
        <FieldColombianDocument />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_colombian_bank_list">
        <BankList />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_uruguayan_document">
        <FieldUruguayanDocument />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_paraguayan_document">
        <FieldParaguayanDocument />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_chilean_document">
        <FieldChileanDocument />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_card_number">
        <FieldCardNumber />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_card_expiry_date">
        <FieldCardExpiryDate />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_card_security_code">
        <FieldCardSecurityCode />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_card_instalments">
        <FieldCardInstalments />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_ewallet_select_wallet">
        <FieldEwalletSelect />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_captcha_token">
        <FieldCaptchaToken />
      </IfFeature>

      <Margin top={2} style={{ width: '100%' }} noFirst>
        <Padding x={1} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',  fontSize: '0.8em' }}>
          <IfFeature allFeatures={allFeatures} wanted="field_accept_terms">
            <FieldAcceptTerms />
          </IfFeature>
          <SafePaymentMessage />
        </Padding>
      </Margin>

      <IfFeature allFeatures={allFeatures} wanted="display_brazilian_debitcard_banks_info">
        <FieldBrazilianDebitcardInfo />
      </IfFeature>

    </div>
  );
}

function useOnSubmit({ onSubmit }: Props) {
  const formHasBeenSent = useFormHasBeenSent();
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const [, setFormStatus] = useFormStatusContext();
  const validation = useValidation();

  useEventBusListener(
    'form:submit',
    async (userInputValues: UserInputValues, onSuccess: EventListener, onError: EventListener) => {
      try {
        const hasErrors = Array.from(document.querySelectorAll('[data-error]')).filter(isElementVisible).length > 0;
        setFormStatus('sending');
        setHasScrolled(false);
        const success = await onSubmit(formHasBeenSent, hasErrors);
        setFormStatus('sent');
        onSuccess(success);
      } catch (e) {
        onError(e);
      }
    },
    [onSubmit, setFormStatus, formHasBeenSent]
  );

  useLayoutEffect(() => {
    if (!hasScrolled && hasUserInputValidationError(validation)) {
      scrollToFirstFieldWithError();
      setHasScrolled(true);
    }
  }, [hasScrolled, setHasScrolled, validation]);
}
